<template>
  <TransitionRoot appear as="template" :show="isVisible">
    <Dialog as="div" class="relative z-[10000]" @close="closeDialog">
      <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100"
        leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-black/40 backdrop-blur-sm" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 " @click.stop>
          <TransitionChild as="template" enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel
              class="relative transform overflow-hidden rounded-lg bg-white shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              <div class="bg-white">
                <div class="flex items-center justify-between p-6 border-b border-gray-200">
                  <div class="flex items-center space-x-3">
                    <div class="flex-shrink-0">
                      <svg class="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <h3 class="text-lg font-semibold leading-6 text-gray-900">
                      Delete
                      <span class="font-bold text-gray-700">{{ elementCategory }}</span>
                    </h3>
                  </div>
                  <button type="button" @click="closeDialog" :disabled="isLoading"
                    class="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500">
                    <span class="sr-only">Close</span>
                    <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div class="p-6">
                  <div class="text-center">
                    <p class="text-base text-gray-600 mb-2">
                      Are you sure you want to delete this {{ elementCategory.toLowerCase() }}?
                    </p>
                    <p class="text-sm text-gray-500">This action cannot be undone.</p>
                  </div>
                </div>

                <div class="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                  <button @click="closeDialog" type="button" :disabled="isLoading"
                    class="cursor-pointer inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed">
                    Cancel
                  </button>
                  <button @click="handleDelete" type="button" :disabled="isLoading"
                    class="cursor-pointer inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed">
                    <template v-if="isLoading">
                      <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                        <path class="opacity-75" fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Deleting...
                    </template>
                    <template v-else>Delete</template>
                  </button>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { ref } from 'vue';
import { projectFirestore, functions, projectAuth } from '@/firebase/config';
import { useAuthStore } from '@/stores/auth';
import { httpsCallable } from 'firebase/functions';
import useStorage from '@/composables/useStorage';
import { doc, getDoc, deleteDoc } from 'firebase/firestore';

const props = defineProps({
  deleteId: { type: String, required: true },
  elementName: { type: String, required: true },
  elementCategory: { type: String, required: true },
  isLastRecordOnPage: { type: Boolean, default: false },
  createdByAdmin: { type: Boolean, default: false },
  isVisible: { type: Boolean, default: false },
  collectionName: { type: String, required: true },
});

const emit = defineEmits(['onClose', 'onEmitHandleEvent']);

const authStore = useAuthStore();
const { removeImage } = useStorage();
const deleteUser = httpsCallable(functions, 'deleteUserV2');
const isLoading = ref(false);

const closeDialog = () => {
  isLoading.value = false;
  emit('onEmitHandleEvent', 'close');
};

const handleDelete = async () => {
  if (isLoading.value) return;

  try {
    isLoading.value = true;


    // Determine current user uid (projectAuth may lag briefly after sign-in)
    const currentUid = (projectAuth && projectAuth.currentUser && projectAuth.currentUser.uid)
      || authStore.userId
      || localStorage.getItem('userId');
    if (!currentUid) {
      throw new Error('You must be signed in to delete this item.');
    }

    if (typeof props.collectionName !== 'string') {
      throw new Error(`Invalid collectionName: ${props.collectionName}`);
    }

    if (props.createdByAdmin) {
      throw new Error('Cannot delete items created by administrator');
    }

    const docRef = doc(projectFirestore, props.collectionName, props.deleteId);
    const docSnapshot = await getDoc(docRef);

    if (!docSnapshot.exists()) {
      throw new Error(`${props.elementCategory} not found`);
    }

    const docData = docSnapshot.data() || {};

    if (props.collectionName === 'blogs') {
      // console.log('Deleting blog with simple auth check');
    } else {
      if (!projectAuth.currentUser || !projectAuth.currentUser.uid) {
        throw new Error('You do not have permission to delete this item.');
      }
    }

    // Delete associated images based on collection type
    if (props.collectionName === 'projects' && Array.isArray(docData.imagePaths)) {
      for (const imagePath of docData.imagePaths) {
        await removeImage(imagePath).catch((err) => console.warn(`Image deletion failed for ${imagePath}:`, err));
      }
    } else if (props.collectionName === 'services' && docData.imageURL) {
      const imagePath = `services/${props.deleteId}`;
      await removeImage(imagePath).catch((err) => console.warn('Image deletion failed:', err));
    } else if (props.collectionName === 'users' && docData.imageURL) {
      const imagePath = `users/${props.deleteId}`;
      await removeImage(imagePath).catch((err) => console.warn('Image deletion failed:', err));
    }

    // Delete the document
    await deleteDoc(docRef);

    // Handle user deletion via Cloud Function if applicable
    if (props.elementCategory === 'User') {
      const { data } = await deleteUser({ uid: props.deleteId });
      if (!data.success) {
        throw new Error(data.error || 'Failed to delete user via function');
      }
    }

    console.log('Delete successful:', props.deleteId);
    emit('onEmitHandleEvent', 'deleted');
  } catch (error) {
    console.error('Delete error:', error);
    emit('onEmitHandleEvent', 'error', true, error.message || 'Failed to delete. Please try again.');
  } finally {
    isLoading.value = false;
  }
};
</script>